import { FormArray, FormControl, FormGroup, Validators, ValidatorFn, AbstractControl } from '@angular/forms';
import { AuthenticationMethodsTemporaryAccessPassSchema } from './model';
import { atLeastOneGroupValidator } from '../authentication-method-policy-utlity';

// Custom validator to check if minimum lifetime is less than or equal to maximum
function minimumLifetimeLessThanMaxValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        if (!control.parent) {
            return null;
        }

        const minimumLifetime = control.value;
        const maximumLifetime = control.parent.get('maximumLifetimeInMinutes')?.value;

        if (
            minimumLifetime === null ||
            minimumLifetime === undefined ||
            maximumLifetime === null ||
            maximumLifetime === undefined
        ) {
            return null;
        }

        if (minimumLifetime > maximumLifetime) {
            return { greaterThanMaximum: { max: maximumLifetime, actual: minimumLifetime } };
        }

        return null;
    };
}

// Custom validator to check if maximum lifetime is greater than or equal to minimum
function maximumLifetimeGreaterThanMinValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        if (!control.parent) {
            return null;
        }

        const maximumLifetime = control.value;
        const minimumLifetime = control.parent.get('minimumLifetimeInMinutes')?.value;

        if (
            maximumLifetime === null ||
            maximumLifetime === undefined ||
            minimumLifetime === null ||
            minimumLifetime === undefined
        ) {
            return null;
        }

        if (maximumLifetime < minimumLifetime) {
            return { lessThanMinimum: { min: minimumLifetime, actual: maximumLifetime } };
        }

        return null;
    };
}

// Custom validator to check if default lifetime is between min and max
function defaultLifetimeBetweenMinMaxValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        if (!control.parent) {
            return null;
        }

        const defaultLifetime = control.value;
        const minimumLifetime = control.parent.get('minimumLifetimeInMinutes')?.value;
        const maximumLifetime = control.parent.get('maximumLifetimeInMinutes')?.value;

        if (
            defaultLifetime === null ||
            defaultLifetime === undefined ||
            minimumLifetime === null ||
            minimumLifetime === undefined ||
            maximumLifetime === null ||
            maximumLifetime === undefined
        ) {
            return null;
        }

        if (defaultLifetime < minimumLifetime) {
            return { belowMinimum: { min: minimumLifetime, actual: defaultLifetime } };
        }

        if (defaultLifetime > maximumLifetime) {
            return { aboveMaximum: { max: maximumLifetime, actual: defaultLifetime } };
        }

        return null;
    };
}

export function initAuthenticationMethodsTemporaryAccessPassForm(
    schema: AuthenticationMethodsTemporaryAccessPassSchema,
): FormGroup {
    const includedGroups = schema.properties?.includeTargets?.items?.oneOf;
    const includedGroupsFormArray = new FormArray([]);
    if (includedGroups?.length > 0) {
        includedGroups.forEach((group) => {
            const groupControl = new FormGroup({
                id: new FormControl(group.properties.id.const),
            });
            includedGroupsFormArray.push(groupControl);
        });
    }

    const formGroup = new FormGroup({
        state: new FormControl(schema.properties.state.const),
        minimumLifetimeInMinutes: new FormControl(schema.properties?.minimumLifetimeInMinutes?.const ?? 60, [
            Validators.required,
            Validators.min(10),
            Validators.max(43200),
            minimumLifetimeLessThanMaxValidator(),
        ]),
        maximumLifetimeInMinutes: new FormControl(schema.properties?.maximumLifetimeInMinutes?.const ?? 480, [
            Validators.required,
            Validators.min(10),
            Validators.max(43200),
            maximumLifetimeGreaterThanMinValidator(),
        ]),
        defaultLifetimeInMinutes: new FormControl(schema.properties?.defaultLifetimeInMinutes?.const ?? 60, [
            Validators.required,
            Validators.min(10),
            Validators.max(43200),
            defaultLifetimeBetweenMinMaxValidator(),
        ]),
        defaultLength: new FormControl(schema.properties?.defaultLength?.const ?? 8, [
            Validators.required,
            Validators.min(8),
            Validators.max(48),
        ]),
        isUsableOnce: new FormControl(schema.properties?.isUsableOnce?.const ?? false),

        includeTargets: new FormGroup(
            {
                includedGroups: includedGroupsFormArray,

                groupInclusion: new FormControl(
                    schema.properties?.includeTargets?.items?.oneOf?.some(
                        (res) => res?.properties?.id?.const !== 'all_users',
                    ),
                ),
            },
            [atLeastOneGroupValidator()],
        ),

        excludeTargets: new FormControl(
            schema.properties?.excludeTargets?.items?.oneOf?.map((res) => res.properties.id.const) || [],
        ),
    });

    // Re-validate all lifetime fields when any of them changes
    formGroup.get('minimumLifetimeInMinutes')?.valueChanges.subscribe(() => {
        formGroup.get('maximumLifetimeInMinutes')?.updateValueAndValidity({ emitEvent: false });
        formGroup.get('defaultLifetimeInMinutes')?.updateValueAndValidity({ emitEvent: false });
    });

    formGroup.get('maximumLifetimeInMinutes')?.valueChanges.subscribe(() => {
        formGroup.get('minimumLifetimeInMinutes')?.updateValueAndValidity({ emitEvent: false });
        formGroup.get('defaultLifetimeInMinutes')?.updateValueAndValidity({ emitEvent: false });
    });

    formGroup.get('defaultLifetimeInMinutes')?.valueChanges.subscribe(() => {
        // No need to revalidate min/max when default changes
    });

    // Re-validate includeTargets when includedGroups changes
    const includeTargetsGroup = formGroup.get('includeTargets') as FormGroup;
    const includedGroupsArray = includeTargetsGroup?.get('includedGroups') as FormArray;
    if (includedGroupsArray) {
        includedGroupsArray.valueChanges.subscribe(() => {
            includeTargetsGroup?.updateValueAndValidity({ emitEvent: false });
        });
    }

    // Re-validate includeTargets when state changes
    formGroup.get('state')?.valueChanges.subscribe(() => {
        includeTargetsGroup?.updateValueAndValidity({ emitEvent: false });
    });

    return formGroup;
}
